<template>
  <div :class="$b()">
    <h4 :class="$b('head')">
      <span
        v-if="
          ['prescription', 'eyeglasses'].includes(selectedProductType)
        "
        :class="$b('heading')"
      >
        Prescription Lenses
      </span>
      <span v-else :class="$b('heading', { normal: true })">
        Choose Lenses
      </span>
      <span
        v-if="showMissingLensTechMsg"
        :class="
          $b('heading-suffix', {
            error: true,
          })
        "
      >
        Please select a lens type
      </span>
    </h4>
    <div
      v-if="showProductTypeToggle"
      :class="$b('product-type-selector')"
    >
      <EyeglassesToggle
        :selected-product-type="props.selectedProductType"
        @toggled-product-type="emits('toggled-product-type', $event)"
      />
    </div>
    <div :class="$b('body')">
      <div :class="$b('options')">
        <CoreLensTechOptions
          v-if="customisedProduct.isSunglasses"
          :options="sunglassesOptions"
          :active-option="activeOption"
          :error="!!showMissingLensTechMsg"
          :customised-product="customisedProduct"
          @selected="selectedLensTech"
        />
        <EyeglassesLensTechOptions
          v-else-if="customisedProduct.isEyeglasses"
          :options="eyeglassesOptions"
          :active-option="activeOption"
          :error="!!showMissingLensTechMsg"
          @selected="selectedLensTech"
        />
        <PrescriptionLensTechOptions
          v-else-if="customisedProduct.isPrescriptionSunglasses"
          :options="prescriptionOptions"
          :active-option="activeOption"
          :error="!!showMissingLensTechMsg"
          @selected="selectedLensTech"
        />
      </div>
    </div>
  </div>
</template>
<script setup>
import CustomisedProduct from '~/models/CustomisedProduct'
import {
  TAG_PRESCRIPTION,
  TAG_EYEGLASSES,
} from '@/assets/js/constants/commerceTags'

defineOptions({
  name: 'LensTechSelectTabs'
})

const emits = defineEmits(['show-lens-guide', 'change', 'toggled-product-type', 'select-prebuilt'])
const { $track } = useNuxtApp()

function selectedLensTech(option) {
  if (option.disabled || option.isLimitedEdition) return

  $track.product.lensTechSelected(props.customisedProduct, option.tech)

  if (option.isPrebuiltVariant) {
    emits('select-prebuilt', option.variant)
    return
  }

  emits('change', option)
}

const props = defineProps({
  customOptions: { type: Array, default: null },
  customisedProduct: { type: CustomisedProduct, required: true },
  showLensGuideLink: { type: Boolean, default: true },
  showMissingLensTechMsg: { type: Boolean, default: false },
  selectedProductType: { type: String, default: 'sunglasses' }
})

const isLimitedEdition = computed(() => {
  return props.customisedProduct.isLimitedEdition
})

const currentTech = computed(() => {
  if (props.customisedProduct.isPrescriptionSunglassesLens)
    return props.customisedProduct.lensChoice?.lensTech

  return props.customisedProduct.lensTech
})

const allOptions = computed(() => {
  if (props.customOptions) return props.customOptions

  if (props.customisedProduct.product.staticLensTech) {
    const staticLensTech = props.customisedProduct.product.staticLensTech

    return [
      {
        optionTech: {
          ...staticLensTech,
        },
        ...staticLensTech,
        isLimitedEdition: isLimitedEdition.value,
        active: true,
        inStock: true
      },
    ]
  }

  if (props.customisedProduct.isPrebuiltVariant) {
    // Get other prebuilt variants in a matching design
    const matchingPrebuiltVariants = props.customisedProduct.matchingPrebuiltVariantsIgnoringLensTech

    return matchingPrebuiltVariants.map((variant) => {
      const option = Object.entries(variant.choices).find(
        ([_, option]) => option.partType === 'lenses'
      )?.[1]
      if (!option) return

      const lensTech = option.optionTech
      
      return {
        ...option,
        ...lensTech,
        isPrebuiltVariant: true,
        inStock: variant.isInStock,
        preorder: variant.isOnPreorder,
        disabled: (!variant.isInStock && !variant.isOnPreorder) || !variant.price,
        isBlueLightFiltering: false,
        isEyeglasses: false,
        isPrescription: false,
        isPolarised: lensTech.tech?.includes('kop') || lensTech.tech?.includes('thp') || lensTech.tech?.includes('stp'),
        iconSize: lensTech.tech?.includes('snow') ? 'x-large' : 'medium',
        active: option.sku === props.customisedProduct.lensChoice.sku,
        price: variant.price,
        variant
      }
    })
  }
  
  return props.customisedProduct.lensOptions
    .map((option) => {
      const lensSku = props.customisedProduct?.lensChoice?.getSKUWithLensTech(
        option.tech
      )
      const partOption = props.customisedProduct.product.allOptions?.[lensSku]
      const optionExists = !!partOption
      const preorder = partOption?.isOnPreorder && !partOption.preorderAsDelayedDespatch
      const inStock = partOption?.isInStock || (partOption?.isOnPreorder && partOption.preorderAsDelayedDespatch)

      return {
        ...option,
        lensSku,
        disabled: !optionExists || (!inStock && !preorder),
        preorder,
        inStock,
        iconSize: option.tech?.includes('snow') ? 'x-large' : 'medium',
        isPrescription: option.tags.some(
          ({ value }) => value === TAG_PRESCRIPTION
        ),
        isEyeglasses: option.tags.some(({ value }) => value === TAG_EYEGLASSES),
        isPolarised: ['kop', 'thp', 'stp'].some((value) =>
          option.tech.includes(value)
        ),
        isBlueLightFiltering: ['thb', 'stb'].some((value) =>
          option.tech.includes(value)
        ),
        active:
          props.customisedProduct.isLimitedEdition ||
          props.customisedProduct.lensTechSelected
      }
    })
    .filter((option) => isLimitedEdition.value || option.price) // If no price can be determined then this tech isn't available
})

const activeOption = computed(() => {
  if (!props.customisedProduct.lensTechSelected) return {}

  const matchedOption = allOptions.value.find(
    (option) => option.tech === currentTech.value
  )

  if (matchedOption) return matchedOption

  return allOptions.value[0]
})

const sunglassesOptions = computed(() => {
  return allOptions.value.filter((option) => !option?.tags?.length)
})

const prescriptionOptions = computed(() => {
  return allOptions.value.filter((option) => option.isPrescription)
})

const eyeglassesOptions = computed(() => {
  return allOptions.value.filter((option) => option.isEyeglasses)
})

const showProductTypeToggle = computed(() => {
  return (
    props.customisedProduct.isEyeglassesAvailable &&
    ['prescription', 'eyeglasses'].includes(props.selectedProductType)
  )
})
</script>

<style lang="postcss">
.LensTechSelectTabs__head {
  @apply flex justify-between md:px-0;
}

.LensTechSelectTabs__lens-guide {
  @apply text-xs align-baseline font-semibold;
}

.LensTechSelectTabs__heading {
  @apply text-sm font-bold mb-3 block;

  &--normal {
    @apply md:mb-0 md:hidden;
  }
}

.LensTechSelectTabs__heading-suffix {
  @apply font-light text-grey-600 text-xs mb-3 block;
}

.LensTechSelectTabs__heading-suffix--error {
  @apply text-red;
}

.LensTechSelectTabs__product-type-selector {
  @apply mb-3;
}

.LensTechSelectTabs__normal-lens-heading {
  @apply md:hidden;
}



/* Dark Chrome Theme */
.is-dark-chrome-theme {
  .LensTechSelectTabs {
    --vision-slido-bg-colour: theme('colors.grey-dark');
    --vision-slido-border-colour: theme('colors.black');
    --vision-slido-tab-active-background-color: theme('colors.grey.800');
    --vision-slido-tab-active-border-color: theme('colors.black');
  }
}
</style>
